import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import Schedule2EducationDialog from "@/components/Schedule2EducationDialog";
import { ArrowLeft, AlertTriangle, AlertCircle, Plus, Trash2, Pill } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";

const CUSTOM = "__custom__";

let rowSeq = 0;
function emptyRow() {
  rowSeq += 1;
  return { _id: `row-${rowSeq}`, medication: "", strength: "", directions: "", _pick: "" };
}

export default function RefillRequestForm({ onBack, onSubmitted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [knownMeds, setKnownMeds] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);
  // Map of row-index -> eligibility result {is_schedule_2, days_since_last_appointment, warning}
  const [eligibility, setEligibility] = useState({});
  // Flips true the first time any row's medication is detected as Schedule II,
  // which triggers the one-time educational dialog.
  const hasScheduleII = Object.values(eligibility).some(e => e?.is_schedule_2);
  const [form, setForm] = useState({
    hasSideEffects: null, needsMedChange: null,
    changePharmacy: null, pharmacyAddress: "", businessHoursAck: false,
  });
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Preload patient's known medications for the dropdown
  useEffect(() => {
    if (!user) return;
    API.get("/medications").then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      const flat = list.map(m => ({
        medication: m.medication || m.name || "",
        strength: m.strength || m.dosage || "",
        directions: m.directions || m.instructions || "",
      })).filter(m => m.medication.trim());
      // dedupe by name+strength
      const seen = new Set();
      const uniq = flat.filter(m => {
        const key = `${m.medication.toLowerCase()}|${m.strength.toLowerCase()}`;
        if (seen.has(key)) return false;
        seen.add(key); return true;
      });
      setKnownMeds(uniq);
    }).catch(() => {});
  }, [user]);

  // Debounced Schedule II eligibility check per medication row.
  useEffect(() => {
    const timers = rows.map((row, idx) => {
      const name = row.medication.trim();
      if (!name) {
        setEligibility(prev => {
          if (!prev[idx]) return prev;
          const next = { ...prev }; delete next[idx]; return next;
        });
        return null;
      }
      // Skip refetch if same medication name as last result
      if (eligibility[idx]?.medication === name) return null;
      const t = setTimeout(async () => {
        try {
          const { data } = await API.get("/refill-requests/eligibility", { params: { medication: name } });
          setEligibility(prev => ({ ...prev, [idx]: { ...data, medication: name } }));
        } catch { /* ignore */ }
      }, 400);
      return t;
    });
    return () => timers.forEach(t => t && clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map(r => r.medication.trim()).join("|")]);

  const updateRow = (idx, field, value) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const removeRow = (idx) => setRows(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));
  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const pickKnown = (idx, value) => {
    if (value === CUSTOM) {
      setRows(prev => prev.map((r, i) => i === idx ? { ...r, _pick: CUSTOM, medication: "", strength: "", directions: "" } : r));
      return;
    }
    const picked = knownMeds[parseInt(value, 10)];
    if (!picked) return;
    setRows(prev => prev.map((r, i) => i === idx
      ? { ...r, _pick: value, medication: picked.medication, strength: picked.strength, directions: picked.directions }
      : r));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const meds = rows
        .map(r => ({ medication: r.medication.trim(), strength: r.strength.trim(), directions: r.directions.trim() }))
        .filter(r => r.medication);
      await API.post("/refill-requests", {
        patient_name: user.full_name || "",
        medications: meds,
        pharmacy_name: form.changePharmacy === "yes" ? "New Pharmacy" : "Previous Pharmacy",
        pharmacy_address: form.changePharmacy === "yes" ? form.pharmacyAddress : "",
        business_hours_ack: form.businessHoursAck,
        status: "pending",
      });
      onSubmitted();
    } finally { setSubmitting(false); }
  };

  if (step === 1) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 animate-fade-in" data-testid="refill-step-1">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> Back to Refills</button>
        <h2 className="text-xl font-display font-semibold mb-1">Request a Refill</h2>
        <p className="text-sm text-muted-foreground mb-8">Let's make sure this is the right request for you.</p>
        <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
          <p className="text-base font-semibold text-foreground">Are you having any side effects?</p>
          <div className="flex gap-3">
            {["yes", "no"].map(v => (
              <button key={v} onClick={() => update("hasSideEffects", v)} data-testid={`side-effects-${v}`}
                className={`flex-1 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${form.hasSideEffects === v ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          {form.hasSideEffects === "yes" && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 animate-fade-in">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">If you are having <strong>severe side effects</strong>, please stop your medication until you discuss with your provider.</p>
            </div>
          )}
        </div>
        <Button onClick={() => setStep(2)} disabled={form.hasSideEffects === null} className="w-full rounded-xl h-12 text-base mt-6">Continue</Button>
      </div>
    );
  }

  if (step === 2) {
    if (form.needsMedChange === "yes") {
      return (
        <div className="max-w-lg mx-auto px-4 py-6 animate-fade-in">
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4 mt-8">
            <div className="flex justify-center"><div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center"><AlertCircle className="w-7 h-7 text-primary" /></div></div>
            <p className="text-center text-base font-semibold">If you need a medication change, please make an appointment with your provider.</p>
          </div>
          <Button onClick={() => { onBack(); navigate("/appointments"); }} className="w-full rounded-xl h-12 text-base mt-6">I Understand</Button>
        </div>
      );
    }
    return (
      <div className="max-w-lg mx-auto px-4 py-6 animate-fade-in" data-testid="refill-step-2">
        <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
          <p className="text-base font-semibold">Do you feel you need a change in your medication?</p>
          <div className="flex gap-3">
            {["yes", "no"].map(v => (
              <button key={v} onClick={() => update("needsMedChange", v)} data-testid={`med-change-${v}`}
                className={`flex-1 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${form.needsMedChange === v ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={() => setStep(3)} disabled={form.needsMedChange === null} className="w-full rounded-xl h-12 text-base mt-6">Continue</Button>
      </div>
    );
  }

  const validMeds = rows.filter(r => r.medication.trim()).length;
  const canSubmit = validMeds > 0 && form.businessHoursAck && form.changePharmacy !== null && (form.changePharmacy === "no" || form.pharmacyAddress.trim());

  return (
    <div className="max-w-lg mx-auto px-4 py-6 animate-fade-in" data-testid="refill-step-3">
      <button onClick={() => setStep(2)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
      <h2 className="text-xl font-display font-semibold mb-1">Medication Details</h2>
      <p className="text-sm text-muted-foreground mb-5">Request one or more refills in a single request.</p>

      <div className="space-y-4">
        {rows.map((row, idx) => (
          <div key={row._id} className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid={`refill-med-row-${idx}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                <Pill className="w-4 h-4 text-primary" /> Medication {idx + 1}
              </p>
              {rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                  data-testid={`refill-remove-med-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              )}
            </div>

            {knownMeds.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Choose from your list</Label>
                <Select value={row._pick} onValueChange={(v) => pickKnown(idx, v)}>
                  <SelectTrigger className="mt-1.5" data-testid={`refill-med-picker-${idx}`}>
                    <SelectValue placeholder="Select a medication…" />
                  </SelectTrigger>
                  <SelectContent>
                    {knownMeds.map((m, i) => (
                      <SelectItem key={`${m.medication}-${m.strength || ""}-${i}`} value={String(i)}>
                        {m.medication}{m.strength ? ` — ${m.strength}` : ""}
                      </SelectItem>
                    ))}
                    <SelectItem value={CUSTOM}>Other (type manually)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">Medication Name *</Label>
              <Input
                value={row.medication}
                onChange={e => updateRow(idx, "medication", e.target.value)}
                placeholder="e.g., Adderall"
                className="mt-1.5"
                data-testid={`refill-med-name-${idx}`}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Strength / Dosage</Label>
              <Input
                value={row.strength}
                onChange={e => updateRow(idx, "strength", e.target.value)}
                placeholder="e.g., 10mg"
                className="mt-1.5"
                data-testid={`refill-med-strength-${idx}`}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Directions</Label>
              <Input
                value={row.directions}
                onChange={e => updateRow(idx, "directions", e.target.value)}
                placeholder="e.g., Take 1 tablet daily in the morning"
                className="mt-1.5"
                data-testid={`refill-med-directions-${idx}`}
              />
            </div>
            {eligibility[idx]?.warning && (
              <div
                className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 text-amber-900"
                data-testid={`refill-schedule2-warning-${idx}`}
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                <div className="text-xs leading-relaxed">
                  <p className="font-semibold mb-0.5">Schedule II controlled medication</p>
                  <p>{eligibility[idx].warning}</p>
                </div>
              </div>
            )}
            {eligibility[idx]?.is_schedule_2 && !eligibility[idx]?.warning && (
              <p
                className="text-[11px] text-muted-foreground inline-flex items-center gap-1"
                data-testid={`refill-schedule2-ok-${idx}`}
              >
                <Pill className="w-3 h-3" />
                Schedule II — last visit {eligibility[idx].days_since_last_appointment} day(s) ago, within DEA window.
              </p>
            )}
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={addRow}
          className="w-full rounded-xl gap-1.5"
          data-testid="refill-add-med-button"
        >
          <Plus className="w-4 h-4" /> Add another medication
        </Button>

        <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
          <p className="text-sm font-semibold">Would you like to change pharmacies? *</p>
          <div className="flex gap-3">
            {["yes", "no"].map(v => (
              <button key={v} onClick={() => update("changePharmacy", v)}
                className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${form.changePharmacy === v ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}>{v === "yes" ? "Yes" : "No"}</button>
            ))}
          </div>
          {form.changePharmacy === "yes" && <div className="animate-fade-in"><Label className="text-sm font-medium">Pharmacy Address</Label><Input value={form.pharmacyAddress} onChange={e => update("pharmacyAddress", e.target.value)} placeholder="Full pharmacy address" className="mt-1.5" /></div>}
        </div>

        <div className="flex items-start gap-3 p-4 bg-accent rounded-xl">
          <Checkbox id="business-hours" checked={form.businessHoursAck} onCheckedChange={v => update("businessHoursAck", v)} data-testid="business-hours-checkbox" />
          <Label htmlFor="business-hours" className="text-sm leading-relaxed cursor-pointer">I understand that refill requests are reviewed during business hours (Mon-Fri, 9 AM - 5 PM) and may take 1-2 business days to process. *</Label>
        </div>

        <Button onClick={handleSubmit} disabled={!canSubmit || submitting} className="w-full rounded-xl h-12 text-base" data-testid="submit-refill-button">
          {submitting ? "Submitting..." : `Submit Refill Request${validMeds > 1 ? ` (${validMeds} medications)` : ""}`}
        </Button>
      </div>
      <Schedule2EducationDialog triggerOpen={hasScheduleII} />
    </div>
  );
}
